import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SendIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  FormOptional,
} from '@/components/ui/form';
import { Card } from '@/components/ui/card';

import { vendorRequestFormSchema, vendorRequestFormDefaultValues, type IVendorRequestForm } from '../model';

import SummaryDetailCard from './SummaryDetailCard';
import VendorSearchField from './VendorSearchField';

type VendorRequestFormProps = {
  onSubmit: (data: IVendorRequestForm) => Promise<void>;
  onCancel: () => void;
};

const VendorRequestForm = ({ onSubmit, onCancel }: VendorRequestFormProps) => {
  const form = useForm<IVendorRequestForm>({
    resolver: zodResolver(vendorRequestFormSchema),
    defaultValues: vendorRequestFormDefaultValues,
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <Card className="p-5 space-y-4">
          <FormField
            control={form.control}
            name="vendorName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vendor</FormLabel>
                <VendorSearchField value={field.value} onChange={field.onChange} />
                <FormMessage />
                <FormDescription>
                  Search Codekeeper&apos;s vendor directory, or type their name if they&apos;re not listed.
                </FormDescription>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="appName"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between gap-2">
                  <FormLabel className="mb-0">Application name</FormLabel>
                  <FormOptional />
                </div>
                <FormControl>
                  <Input {...field} placeholder="e.g. Nucleus Fraud Engine" />
                </FormControl>
                <FormMessage />
                <FormDescription>
                  The product name, if you know it. The vendor can rename it when they set up the application.
                </FormDescription>
              </FormItem>
            )}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="contactName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. Priya Shah" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact email</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. name@vendor.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>What do you need protected?</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="e.g. Their fraud-detection scoring API, which we depend on for checkout risk decisions."
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>
                  Describe the software in your own words. The vendor will confirm all application details on their
                  side.
                </FormDescription>
              </FormItem>
            )}
          />
        </Card>
        <SummaryDetailCard />
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={form.formState.isSubmitting} disabled={form.formState.isSubmitting}>
            <SendIcon />
            Send request
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default VendorRequestForm;
